import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { DatabaseLoad, GeoCoords } from '@/lib/loadSearch';
import { haversineDistance } from '@/lib/loadSearch';
import { useRealtimeSelfHeal } from '@/hooks/useRealtimeSelfHeal';

export interface BoostedLoad extends DatabaseLoad {
  boost_id: string;
  boost_expires_at: string;
}

interface UseBoostedLoadsParams {
  enabled: boolean;
  equipmentTypes: string[];
  originCoords: GeoCoords | null;
  destCoords: GeoCoords | null;
  originRadius: number;
  destRadius: number;
  hideCanada: boolean;
}

export function useBoostedLoads({
  enabled,
  equipmentTypes,
  originCoords,
  destCoords,
  originRadius,
  destRadius,
  hideCanada,
}: UseBoostedLoadsParams) {
  const [boostedLoads, setBoostedLoads] = useState<BoostedLoad[]>([]);
  const [loading, setLoading] = useState(false);
  const expireCalled = useRef(false);

  // Stable refs for filter values so the fetch callback doesn't change identity
  const filtersRef = useRef({ equipmentTypes, originCoords, destCoords, originRadius, destRadius, hideCanada });
  filtersRef.current = { equipmentTypes, originCoords, destCoords, originRadius, destRadius, hideCanada };

  const fetchBoostedLoads = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);

    // Expire stale boosts (fire-and-forget, once per mount)
    if (!expireCalled.current) {
      expireCalled.current = true;
      Promise.resolve(supabase.rpc('expire_boosts' as any)).catch(() => {});
    }

    const { data, error } = await supabase
      .from('load_boosts')
      .select(`
        id,
        load_id,
        expires_at,
        loads!inner (
          id, load_number, origin_city, origin_state, origin_country,
          origin_latitude, origin_longitude,
          destination_city, destination_state, destination_country,
          destination_latitude, destination_longitude,
          equipment_type, weight_lbs, length_ft, pickup_date, delivery_date,
          rate_amount, total_miles, status, commodity, special_instructions,
          is_public, contact_name, contact_phone, contact_email, posted_at,
          source, company_id, created_by, load_type, rate_type,
          reference_number, payment_terms, pieces, fuel_surcharge_rate,
          accessorial_charges, pickup_time_start, pickup_time_end,
          delivery_time_start, delivery_time_end, contact_phone_secondary,
          companies ( name )
        )
      `)
      .eq('status', 'active')
      .gt('expires_at', new Date().toISOString());

    if (error || !data) {
      setLoading(false);
      return;
    }

    const f = filtersRef.current;

    let results: BoostedLoad[] = data
      .filter((row: any) => row.loads && row.loads.status === 'posted')
      .map((row: any) => ({
        ...row.loads,
        companies: row.loads.companies,
        boost_id: row.id,
        boost_expires_at: row.expires_at,
      }));

    // Equipment filter
    if (f.equipmentTypes.length > 0) {
      const eqLower = f.equipmentTypes.map(e => e.toLowerCase());
      results = results.filter(l => eqLower.includes((l.equipment_type || '').toLowerCase()));
    }

    // Origin radius filter
    if (f.originCoords && f.originRadius > 0) {
      results = results.filter(l => {
        if (!l.origin_latitude || !l.origin_longitude) return false;
        return haversineDistance(f.originCoords!.lat, f.originCoords!.lon, l.origin_latitude, l.origin_longitude) <= f.originRadius;
      });
    }

    // Dest radius filter
    if (f.destCoords && f.destRadius > 0) {
      results = results.filter(l => {
        if (!l.destination_latitude || !l.destination_longitude) return false;
        return haversineDistance(f.destCoords!.lat, f.destCoords!.lon, l.destination_latitude, l.destination_longitude) <= f.destRadius;
      });
    }

    // Hide Canada
    if (f.hideCanada) {
      results = results.filter(l => l.origin_country !== 'Canada' && l.destination_country !== 'Canada');
    }

    setBoostedLoads(results.slice(0, 5));
    setLoading(false);
  }, [enabled]);

  // Polling effect
  useEffect(() => {
    if (!enabled) {
      setBoostedLoads([]);
      return;
    }

    fetchBoostedLoads();
    const interval = setInterval(fetchBoostedLoads, 60_000);
    return () => clearInterval(interval);
  }, [enabled, equipmentTypes.join(','), originCoords?.lat, originCoords?.lon, destCoords?.lat, destCoords?.lon, originRadius, destRadius, hideCanada, fetchBoostedLoads]);

  // Realtime subscription for instant updates
  useEffect(() => {
    if (!enabled) return;

    const channel = supabase
      .channel('boosted-loads-realtime')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'load_boosts' },
        () => { fetchBoostedLoads(); }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'load_boosts' },
        () => { fetchBoostedLoads(); }
      )
      .subscribe((status) => {
        // Self-heal: refetch on (re)subscribe so events dropped during a
        // CHANNEL_ERROR gap don't leave the UI stale.
        if (status === 'SUBSCRIBED') fetchBoostedLoads();
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [enabled, fetchBoostedLoads]);

  useRealtimeSelfHeal(fetchBoostedLoads, enabled);


  return { boostedLoads, loading };
}
